import React, { useEffect, useState, useRef, useCallback } from 'react';
import './SigmaReport.scss';
import { getReportTokens } from 'src/api/reports';
import { PAGE_INFO } from 'src/util/constants';
import { useFmtpNavigate } from 'src/hooks/useFmtpNavigate';
import { Card } from '@mui/material';

type TokensResponse = {
    embedToken: string;
    reportId: string;
    embedUrl: string;
    isPaginated: boolean;
    expiration: string;
};

const INTERVAL_TIME = 55 * 60 * 1000; // 55 minutes

export default function SigmaReport({ currentReport }: { currentReport: string | null }) {
    const fmtpNavigate = useFmtpNavigate();
    const [isLoadingReport, setIsLoadingReport] = useState(false);
    const [errorGettingReport, setErrorGettingReport] = useState(false);
    const [sigmaEmbedURL, setSigmaEmbedURL] = useState<string | null>(null);
    const reportInfo = useRef<TokensResponse | null>(null);

    const updateReport = useCallback(async () => {
        if (!currentReport) {
            reportInfo.current = null;
            setSigmaEmbedURL(null);
            return;
        }

        setIsLoadingReport(true);
        setErrorGettingReport(false);
        try {
            const tokens = await getReportTokens(currentReport);

            reportInfo.current = tokens;
            setSigmaEmbedURL(tokens.embedUrl);
        } catch (err) {
            console.error('Error fetching Sigma tokens', err);
            setErrorGettingReport(true);
        } finally {
            setIsLoadingReport(false);
        }
    }, [currentReport]);

    useEffect(() => {
        reportInfo.current = null;
        updateReport();
    }, [updateReport]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            const info = reportInfo.current;
            if (!info) return;

            if (info.isPaginated) {
                fmtpNavigate(`${PAGE_INFO.reports.href}?reportExpired=true`);
            } else {
                updateReport();
            }
        }, INTERVAL_TIME);

        return () => window.clearInterval(intervalId);
    }, [fmtpNavigate, updateReport]);

    return (
        <>
            {isLoadingReport && <h1>Loading Report…</h1>}
            {errorGettingReport && <h1>There was an error getting this report. If this persists, please contact Support.</h1>}
            {!isLoadingReport && sigmaEmbedURL && (
                <Card className="sigma-report-card">
                    <iframe title="Sigma Report" src={sigmaEmbedURL} allowFullScreen />
                </Card>
            )}
        </>
    );
}
